import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { AridosModalFirmaPapelComponent } from 'src/app/profiles/aridos-mixer/despacho/firma/modal-firma-papel/modal-firma-papel.component';
import { AccountService } from 'src/app/services/auth';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { Order } from 'src/app/shared/models/orders';
import { environment } from 'src/environments/environment';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { SyncService } from 'src/app/services/offline/sync.service';
import {
  SeguimientoAnonimoViajeRequest,
  SeguimientoAnonimoViajeResponse,
  SeguimientoAnonimoViajesGatewayApi,
} from 'src/core/apis/seguimiento-anonimo.viajes';

export type QrState = 'syncing' | 'ready' | 'offline';
export type SignatureTab = 'firma' | 'qr' | 'papel';
export type QrContinuarModalState = 'loading' | 'pending' | 'success' | 'error';

@Component({
    selector: 'app-pedidos-pendiente-qr',
    templateUrl: './pedidos-pendiente-qr.component.html',
    styleUrls: ['./pedidos-pendiente-qr.component.css'],
    standalone: false
})
export class PedidosPendienteQrComponent implements OnInit, OnDestroy {
  @ViewChild('contenidoModal') contenidoModal!: TemplateRef<any>;
  @ViewChild('contenidoModalPapel') contenidoModalPapel!: TemplateRef<any>;
  @ViewChild('contenidoModalQrContinuar') contenidoModalQrContinuar!: TemplateRef<any>;
  dialogRef: MatDialogRef<any>;

  @Input() order: Order;
  @Input() initialTab: SignatureTab = 'firma';
  @Output() toNextStep = new EventEmitter<void>();

  activeTab: SignatureTab = 'firma';
  isNotificationVisible = false;

  tituloModal: string;
  descripcionModal: string;
  linkQr: string;

  readonly showClientQr = environment.seguimientoAnonimoEnabled !== false;

  qrState: QrState = 'syncing';

  PasoModal = {
    preguntaModal: false,
    ConfirmacionModal: false,
  };

  papelPhotoBase64: string | null = null;
  papelLoading = false;
  papelSavedOffline = false;

  qrContinuarModalState: QrContinuarModalState = 'loading';
  qrContinuarError = '';

  private destroyed = false;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private orderService: OrderService,
    private accountUser: AccountService,
    private offlineStorage: OfflineStorageService,
    private syncService: SyncService,
    private viajesGateway: SeguimientoAnonimoViajesGatewayApi,
  ) {}

  ngOnInit(): void {
    this.activeTab = this.initialTab ?? 'firma';
    if (!this.showClientQr) {
      return;
    }
    const path = environment.seguimientoAnonimoPublicPath ?? '/Seguimiento/3';
    const origin = window.location.origin;
    const params = this.buildSeguimientoAnonimoParams();
    const key = encodeURIComponent(params.key);
    const tkt_h = encodeURIComponent(params.tkt_h);
    const id1 = encodeURIComponent(params.id1);
    this.linkQr = `${origin}${path}?key=${key}&tkt_h=${tkt_h}&id1=${id1}`;
    this.qrState = this.offlineStorage.isOnline() ? 'syncing' : 'offline';
    this.startMonitoring();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  setActiveTab(tab: SignatureTab): void {
    this.activeTab = tab;
  }

  savePapelFromGallery(): void {
    if (!this.papelPhotoBase64 || this.papelLoading) {
      return;
    }

    const tkt = this.order.tkt.toString();
    const orderNumber = this.order.orderNumber.toString();
    const rut = this.accountUser.userValue.RUT_USUARIO;
    const name = this.accountUser.userValue.NOMBRE_USUARIO;

    this.papelLoading = true;
    this.orderService
      .setSignedGuide(tkt, orderNumber, rut, name, this.papelPhotoBase64, '')
      .pipe(take(1))
      .subscribe({
        next: (submitResult) => {
          this.papelLoading = false;
          this.papelSavedOffline = submitResult.status === 'queued';
          this.papelPhotoBase64 = null;
          this.dialogRef = this.dialog.open(this.contenidoModalPapel, {
            maxWidth: '100%',
          });
        },
        error: () => {
          this.papelLoading = false;
          alert('Ocurrió un error al enviar la firma.');
        },
      });
  }

  onCierrenModalPapel(): void {
    this.dialogRef?.close();
    this.papelPhotoBase64 = null;
    if (this.papelSavedOffline) {
      this.goBackToPedidosPendientes();
      return;
    }
    this.goBack();
  }

  switchToFirmaTab(): void {
    this.activeTab = 'firma';
  }

  private async startMonitoring(): Promise<void> {
    if (this.offlineStorage.isOnline()) {
      this.syncService.flushNow();
    }
    await this.checkAndUpdateQrState();

    while (!this.destroyed && this.qrState !== 'ready') {
      await new Promise<void>((resolve) => setTimeout(resolve, 300));
      if (this.destroyed) break;
      if (this.offlineStorage.isOnline() && this.qrState === 'offline') {
        this.syncService.flushNow();
      }
      await this.checkAndUpdateQrState();
    }
  }

  private async checkAndUpdateQrState(): Promise<void> {
    if (!this.offlineStorage.isOnline()) {
      this.qrState = 'offline';
      return;
    }

    const tkt = this.order.tkt.toString();
    const queue = await this.offlineStorage.getQueue();
    const entry = queue.find((e) => e.tkt === tkt);

    const nonTerminalCount =
      entry?.operations.filter(
        (op) =>
          op.type !== 'signed' &&
          op.type !== 'signedCanvas' &&
          op.type !== 'unsigned'
      ).length ?? 0;

    const newState: QrState = nonTerminalCount === 0 ? 'ready' : 'syncing';

    if (newState === 'ready' && this.qrState !== 'ready') {
      this.isNotificationVisible = true;
    }

    this.qrState = newState;
  }

  private convertBase64(a: string) {
    return btoa(a.replace('-', ''));
  }

  private buildSeguimientoAnonimoParams(): SeguimientoAnonimoViajeRequest {
    return {
      key: this.accountUser.userValue.KEY,
      tkt_h: this.order.tkt.toString(),
      id1: this.convertBase64(this.accountUser.userValue.RUT_USUARIO),
    };
  }

  private getMxCliFirmaFromResponse(raw: SeguimientoAnonimoViajeResponse): string {
    const viajesh = (raw.datos?.[0] as { VIAJESH?: Array<{ MX_CLI_FIRMA?: string }> } | undefined)
      ?.VIAJESH;
    return viajesh?.[0]?.MX_CLI_FIRMA ?? '';
  }

  onContinuar(): void {
    if (!this.offlineStorage.isOnline()) {
      alert('Sin conexión. No es posible consultar la firma.');
      return;
    }

    this.qrContinuarModalState = 'loading';
    this.qrContinuarError = '';
    this.dialogRef = this.dialog.open(this.contenidoModalQrContinuar, {
      maxWidth: '100%',
      disableClose: true,
    });
    this.consultarFirmaAnonima();
  }

  consultarFirmaAnonima(): void {
    this.qrContinuarModalState = 'loading';
    this.qrContinuarError = '';

    this.viajesGateway
      .post(this.buildSeguimientoAnonimoParams())
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (!response?.success) {
            this.qrContinuarModalState = 'error';
            this.qrContinuarError = 'No fue posible consultar la firma.';
            return;
          }

          const firma = this.getMxCliFirmaFromResponse(response);
          this.qrContinuarModalState = firma === '' ? 'pending' : 'success';
        },
        error: () => {
          this.qrContinuarModalState = 'error';
          this.qrContinuarError = 'No fue posible consultar la firma.';
        },
      });
  }

  onCancelarModalQrContinuar(): void {
    this.dialogRef?.close();
  }

  onConsultarNuevamenteQr(): void {
    this.consultarFirmaAnonima();
  }

  onAceptarFirmaQrExito(): void {
    this.dialogRef?.close();
    this.goBack();
  }

  onOpenModal(): void {
    this.onOpenModalGuiaNoFirmada();
  }

  onOpenModalGuiaNoFirmada(): void {
    this.PasoModal.preguntaModal = false;
    this.PasoModal.ConfirmacionModal = false;
    this.dialogRef = this.dialog.open(this.contenidoModal, {
      maxWidth: '100%',
    });
    this.guiaNoFirmada();
  }

  guiaNoFirmada(): void {
    this.PasoModal.preguntaModal = true;
    this.tituloModal = '¿Deseas confirmar la guía no firmada?';
    this.descripcionModal =
      'Una vez confirmada no será posible cambiar la información.';
  }

  confirmarGuiaNoFirmada(): void {
    this.PasoModal.preguntaModal = false;
    this.PasoModal.ConfirmacionModal = true;

    this.tituloModal = 'Guía no firmada enviada';
    this.descripcionModal =
      'La información fue enviada al cliente y al despacho para su posterior firma.';

    this.orderService
      .setUnsignedGuide(this.order.tkt.toString(), this.order.orderNumber.toString())
      .subscribe();
  }

  firmaPapel(): void {
    const dialogRef = this.dialog.open(AridosModalFirmaPapelComponent, {
      disableClose: true,
      data: {
        tkt: this.order.tkt,
        orderNumber: this.order.orderNumber,
        rut: this.accountUser.userValue.RUT_USUARIO,
        name: this.accountUser.userValue.NOMBRE_USUARIO,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.goBack();
      }
    });
  }

  hideNotification(): void {
    this.isNotificationVisible = false;
  }

  withoutInternet(): void {
    this.toNextStep.emit();
  }

  onCierrenModal(): void {
    const irAlHistorial = this.PasoModal.ConfirmacionModal;
    this.dialogRef?.close();
    if (irAlHistorial) {
      this.goBack();
    }
  }

  goBack(): void {
    this.router.navigate(['/pedidosEntregados']);
  }

  goBackToPedidosPendientes(): void {
    this.router.navigate(['/pedidospendiente']);
  }
}
